import { useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';

import { endOfMonth, formatISO, parseISO } from 'date-fns';

import * as AppStore from '@asteria/datalayer/stores/app';

import { useTransactions as useScenarioTransactions } from '../../../../utils/operations';

const extractIds = (items) => {
	return new Set(items.map((t) => t._id));
};

const areSetsEqual = (a, b) => {
	if (a.size !== b.size) return false;
	for (const val of a) {
		if (!b.has(val)) return false;
	}
	return true;
};

export function useData(options) {
	let { type, types = [type], date, tag } = options;
	type = types[0];

	const tagId = useSelector((store) => {
		if (tag === '$deposit' || tag === '$withdraw') {
			return null;
		}

		const tags = AppStore.selectors.tags(store);
		const storedTag = tags.find((item) => item?.name === tag);

		return storedTag?._id ?? storedTag?.id ?? '$unknown';
	});

	const total = useScenarioTransactions({
		types: types,
		tagId: tagId,
		startDate: formatISO(parseISO(date), { representation: 'date' }),
		endDate: formatISO(endOfMonth(parseISO(date)), {
			representation: 'date',
		}),
	}).reduce(
		(acc, curr) => {
			if (curr.payload.type === 'DEPOSIT') {
				acc[0] += curr.payload.total;
			} else {
				acc[1] += curr.payload.total;
			}

			return acc;
		},
		[0, 0],
	);

	return useMemo(() => {
		if (types?.length > 1) {
			return { total: total[0] - total[1] };
		}

		return { total: type === 'deposit' ? total[0] : total[1] };
	}, [total, types?.length, type]);
}

export function useTransactions({ types, date, tag }) {
	const tagId = useSelector((store) => {
		if (tag === '$deposit' || tag === '$withdraw') {
			return null;
		}

		const tags = AppStore.selectors.tags(store);
		const storedTag = tags.find((item) => item?.name === tag);

		return storedTag?._id ?? storedTag?.id ?? '$unknown';
	});

	const operations = useScenarioTransactions({
		types: types,
		tagId: tagId,
		startDate: formatISO(parseISO(date), { representation: 'date' }),
		endDate: formatISO(endOfMonth(parseISO(date)), {
			representation: 'date',
		}),
	});

	const [stableOperations, setStableOperations] = useState(() => operations);
	const [stableIds, setStableIds] = useState(() => extractIds(operations));
	const prevIdsRef = useRef(stableIds);

	const currentIds = useMemo(() => extractIds(operations), [operations]);

	useEffect(() => {
		if (!areSetsEqual(prevIdsRef.current, currentIds)) {
			setStableIds(currentIds);
			setStableOperations(operations);
			prevIdsRef.current = currentIds;
		}
	}, [currentIds, operations]);

	return stableOperations;
}
